import type { AppRole, AustralianState } from '@empower-safety/shared-types';
import { AUSTRALIAN_STATES } from '@empower-safety/shared-types';
import {
  AlertTriangle,
  BellRing,
  Briefcase,
  CheckCircle2,
  Clock,
  HeartPulse,
  Loader2,
  Mail,
  MailPlus,
  Pencil,
  Phone,
  Power,
  PowerOff,
  RefreshCw,
  ShieldAlert,
  Trash2,
  Undo2,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  type CreateInvitationOutcome,
  type InvitationDto,
  type InviteItem,
  useCreateInvitations,
  usePendingInvitations,
  useResendInvitation,
  useRevokeInvitation,
} from '../api/useInvitations.js';
import { useJobTitles } from '../api/useJobTitles.js';
import { useMe } from '../api/useMe.js';
import { type RosterMember, useRoster } from '../api/useRoster.js';
import { type ActiveShiftDto, useActiveShifts, useForceEndShift } from '../api/useShifts.js';
import { useTeamsList } from '../api/useTeams.js';
import { useUpdateUser } from '../api/useUserEdit.js';
import { useDeactivateUser, useDeleteUser, useReactivateUser } from '../api/useUserLifecycle.js';
import { useRequestWelfareCheck } from '../api/useWelfare.js';
import { EmergencyContactsModal } from '../components/EmergencyContactsModal.js';
import { EmptyState } from '../components/EmptyState.js';
import { ExternalIdentitiesSection } from '../components/ExternalIdentitiesSection.js';
import { RoleGate } from '../components/RoleGate.js';
import { cn } from '../lib/cn.js';
import { formatAlarmType, formatRelative } from '../lib/format.js';

// The URL we hand to Clerk as the invitation-email redirect target. Clerk
// emails contain a `__clerk_ticket` that must be consumed by a Clerk-enabled
// page to complete the invitation.
//
// We point at the `/invite` path on the dashboard host. On mobile, that path
// is claimed by the Empower Safety app via Universal Links (iOS) and App
// Links (Android) — see apps/dashboard/public/.well-known/ + apps/mobile/
// app.json — so a worker tapping the email link on their phone opens the
// native app's invite handler at apps/mobile/app/invite.tsx, which consumes
// the ticket via Clerk's sign-up flow. On desktop or on a phone without the
// app installed, the same URL falls through to the dashboard SPA; the
// existing `<SignedOut>` `<SignIn>` shell picks up the ticket from the URL
// query and completes the invitation in the browser.
//
// Using window.location.origin keeps this correct for local dev
// (http://localhost:5173), preview deploys, and prod regardless of how the
// dashboard is served.
function clerkRedirectUrl(): string {
  return typeof window !== 'undefined' ? `${window.location.origin}/invite` : '';
}

type Filter = 'all' | AppRole;

const tabs: { key: Filter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'worker', label: 'Workers' },
  { key: 'monitor', label: 'Monitors' },
  { key: 'admin', label: 'Admins' },
];

type LifecycleAction = 'deactivate' | 'reactivate' | 'delete';

export default function Roster() {
  const [filter, setFilter] = useState<Filter>('all');
  const [welfareTarget, setWelfareTarget] = useState<RosterMember | null>(null);
  const [editTarget, setEditTarget] = useState<RosterMember | null>(null);
  const [contactsTarget, setContactsTarget] = useState<RosterMember | null>(null);
  const [lifecycleTarget, setLifecycleTarget] = useState<{
    member: RosterMember;
    action: LifecycleAction;
  } | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const me = useMe();
  const query = useRoster();
  const pending = usePendingInvitations();
  const members = query.data?.members ?? [];
  const pendingInvites = pending.data?.invitations ?? [];

  const filtered = useMemo(
    () => (filter === 'all' ? members : members.filter((m) => m.app_role === filter)),
    [members, filter],
  );

  const activeWithAlarmFirst = useMemo(() => {
    return [...filtered].sort((a, b) => {
      if (a.active_alarm_id && !b.active_alarm_id) return -1;
      if (!a.active_alarm_id && b.active_alarm_id) return 1;
      return a.display_name.localeCompare(b.display_name);
    });
  }, [filtered]);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-5 flex items-end justify-between gap-4">
        <div>
          <span className="eyebrow">Field</span>
          <h1 className="mt-1 font-heading text-[22px] font-bold tracking-tightest text-ink">
            Users
          </h1>
          <p className="text-sm text-ink-faint">
            {query.data?.members.length ?? 0} members
            {pendingInvites.length > 0 && ` · ${pendingInvites.length} pending`}
            {' · '}workers with active alarms sorted first
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex gap-0.5 rounded-md border border-line bg-surface-1 p-1 shadow-xs">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setFilter(t.key)}
                className={cn(
                  'rounded-sm px-3.5 py-1.5 text-xs font-semibold transition',
                  filter === t.key ? 'bg-brand-purple text-white' : 'text-ink-faint hover:text-ink',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
          <RoleGate allow={['admin']} fallback={null}>
            <button
              type="button"
              onClick={() => setShowInvite(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-deep-purple"
            >
              <UserPlus className="h-4 w-4" />
              Invite
            </button>
          </RoleGate>
        </div>
      </div>

      {pendingInvites.length > 0 && <PendingInvitesPanel invitations={pendingInvites} />}

      {query.isError && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          Failed to load roster: {(query.error as Error).message}
        </div>
      )}

      <ActiveShiftsPanel />

      {!query.isLoading && filtered.length === 0 && (
        <EmptyState
          icon={Users}
          title="No members in this view"
          description={
            filter === 'all'
              ? 'Invite workers via the Clerk dashboard and they will appear here once they accept and sign in.'
              : 'Switch filters or assign roles via organisation settings.'
          }
        />
      )}

      {activeWithAlarmFirst.length > 0 && (
        <div className="panel">
          <table className="min-w-full divide-y divide-line">
            <thead className="bg-surface-3">
              <tr>
                <Th>Worker</Th>
                <Th>Role</Th>
                <Th>Teams</Th>
                <Th>Safety state</Th>
                <Th>Last seen</Th>
                <Th>Joined</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line bg-white">
              {activeWithAlarmFirst.map((m) => (
                <Row
                  key={m.user_id}
                  m={m}
                  isSelf={me.data?.user_id === m.user_id}
                  onRequestWelfareCheck={(member) => setWelfareTarget(member)}
                  onEdit={(member) => setEditTarget(member)}
                  onManageContacts={(member) => setContactsTarget(member)}
                  onLifecycle={(member, action) => setLifecycleTarget({ member, action })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {welfareTarget && (
        <WelfareCheckModal target={welfareTarget} onClose={() => setWelfareTarget(null)} />
      )}
      {showInvite && <InviteModal onClose={() => setShowInvite(false)} />}
      {editTarget && <EditUserModal target={editTarget} onClose={() => setEditTarget(null)} />}
      {contactsTarget && (
        <EmergencyContactsModal
          userId={contactsTarget.user_id}
          userName={contactsTarget.display_name}
          onClose={() => setContactsTarget(null)}
        />
      )}
      {lifecycleTarget && (
        <LifecycleModal
          target={lifecycleTarget.member}
          action={lifecycleTarget.action}
          onClose={() => setLifecycleTarget(null)}
        />
      )}
    </div>
  );
}

// Active-shifts panel. Sits between the error banner and the worker table.
// Shows who's currently on shift, how long they've been on, vehicle they're
// in, and a "Force end" action gated to monitor/admin (the API enforces it
// too — this is just the UI affordance). Sorted oldest-first by the API so
// overdue end-shift candidates surface near the top.
function ActiveShiftsPanel() {
  const query = useActiveShifts();
  const [forcing, setForcing] = useState<ActiveShiftDto | null>(null);
  const shifts = query.data?.shifts ?? [];

  if (query.isLoading) return null;
  if (query.isError) {
    return (
      <div className="mb-4 rounded-md border border-amber-200 bg-amber-50/40 px-4 py-2 text-sm text-amber-800">
        Couldn’t load active shifts: {(query.error as Error).message}
      </div>
    );
  }
  if (shifts.length === 0) return null;

  return (
    <div className="mb-4 overflow-hidden rounded-lg border border-line bg-surface-1 shadow-panel">
      <div className="flex items-center justify-between border-b border-line bg-surface-3 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-ink-mute">
        <span className="inline-flex items-center gap-2">
          <Briefcase className="h-3.5 w-3.5 text-brand-purple" />
          Currently on shift · {shifts.length}
        </span>
      </div>
      <ul className="divide-y divide-line">
        {shifts.map((s) => (
          <ActiveShiftRow key={s.id} shift={s} onForceEnd={() => setForcing(s)} />
        ))}
      </ul>
      {forcing && <ForceEndShiftModal shift={forcing} onClose={() => setForcing(null)} />}
    </div>
  );
}

function ActiveShiftRow({
  shift,
  onForceEnd,
}: {
  shift: ActiveShiftDto;
  onForceEnd: () => void;
}) {
  // Compute "on-shift duration" client-side so the row ticks even between
  // the 60s server polls. It's a UI nicety; authoritative duration comes
  // from the server's summary at end time.
  const minutes = Math.floor((Date.now() - new Date(shift.started_at).getTime()) / 60_000);
  const isLong = minutes >= 8 * 60; // 8h — overdue end-shift candidate
  return (
    <li className="flex items-center gap-3 px-4 py-2.5">
      <div className="flex flex-1 items-center gap-3 min-w-0">
        <div
          className={cn(
            'h-2 w-2 rounded-full',
            isLong ? 'bg-warning' : 'bg-success animate-live-pulse',
          )}
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-ink">{shift.user_display_name}</div>
          <div className="truncate text-xs text-ink-faint">
            <Clock className="-mt-0.5 mr-1 inline h-3 w-3" />
            On for {formatDuration(minutes)}
            {shift.vehicle ? <> · {shift.vehicle}</> : null}
          </div>
        </div>
        {isLong && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 ring-1 ring-inset ring-amber-200">
            <AlertTriangle className="h-3 w-3" />
            Long shift
          </span>
        )}
      </div>
      <RoleGate allow={['monitor', 'admin']} fallback={null}>
        <button
          type="button"
          onClick={onForceEnd}
          className="inline-flex items-center gap-1 rounded-md border border-line bg-surface-1 px-2.5 py-1 text-xs font-medium text-ink-dim transition hover:border-line-strong hover:bg-surface-3 hover:text-ink"
        >
          <PowerOff className="h-3 w-3" />
          Force end
        </button>
      </RoleGate>
    </li>
  );
}

function ForceEndShiftModal({
  shift,
  onClose,
}: {
  shift: ActiveShiftDto;
  onClose: () => void;
}) {
  const force = useForceEndShift();
  const [reason, setReason] = useState('');

  function submit() {
    force.mutate({ id: shift.id, reason: reason.trim() || null }, { onSuccess: onClose });
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="force-end-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        role="document"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg border border-line bg-surface-1 p-5 shadow-panel"
      >
        <h2
          id="force-end-title"
          className="font-heading text-base font-bold tracking-tight text-ink"
        >
          Force-end {shift.user_display_name}’s shift?
        </h2>
        <p className="mt-1 text-sm text-ink-faint">
          They’re marked as on-shift since{' '}
          <span className="font-mono text-ink-dim">
            {new Date(shift.started_at).toLocaleString()}
          </span>
          . This closes their shift now and tags it as monitor-ended in the audit log.
        </p>
        <label htmlFor="force-end-reason" className="mt-4 block text-xs font-medium text-ink-faint">
          Reason (optional)
        </label>
        <textarea
          id="force-end-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Worker forgot to end-shift; phone went flat at site."
          rows={3}
          maxLength={2000}
          className="mt-1 w-full resize-none rounded-md border border-line-strong px-3 py-2 text-sm placeholder-gray-400 focus:border-brand-purple focus:outline-none focus:ring-1 focus:ring-brand-purple"
        />
        {force.isError && (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
            {(force.error as Error).message}
          </div>
        )}
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={force.isPending}
            className="rounded-md border border-line bg-surface-1 px-3 py-1.5 text-sm font-medium text-ink-dim transition hover:border-line-strong hover:bg-surface-3 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={force.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-purple px-3 py-1.5 text-sm font-medium text-white shadow-glow-primary transition hover:bg-brand-purple/90 disabled:opacity-50"
          >
            <PowerOff className="h-3.5 w-3.5" />
            {force.isPending ? 'Ending…' : 'Force end shift'}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function PendingInvitesPanel({ invitations }: { invitations: InvitationDto[] }) {
  return (
    <div className="mb-4 overflow-hidden rounded-lg border border-amber-200 bg-amber-50/40 shadow-panel">
      <div className="border-b border-amber-100 bg-amber-50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-amber-800">
        <Mail className="mr-1 inline h-3 w-3" />
        Pending invitations ({invitations.length})
      </div>
      <ul className="divide-y divide-amber-100">
        {invitations.map((inv) => (
          <PendingRow key={inv.id} inv={inv} />
        ))}
      </ul>
    </div>
  );
}

function PendingRow({ inv }: { inv: InvitationDto }) {
  const revoke = useRevokeInvitation();
  const resend = useResendInvitation();
  // Surface a hint when the original Clerk invite never made it (the row was
  // created locally but clerkInvitationId never got populated — usually means
  // createOrganizationInvitation failed silently in the first send). The
  // recipient will get no email in this state; Resend recreates the Clerk side.
  const clerkMissing = !inv.clerk_invitation_id;
  return (
    <li className="flex items-center gap-3 px-4 py-2 text-sm">
      <Mail className="h-4 w-4 text-amber-700" />
      <div className="flex-1 min-w-0">
        <div className="truncate font-medium text-ink">{inv.email}</div>
        <div className="flex items-center gap-2 text-[11px] text-ink-faint">
          <span className="capitalize">{inv.app_role}</span>
          {inv.team_ids.length > 0 && (
            <>
              <span>·</span>
              <span>
                {inv.team_ids.length} team{inv.team_ids.length === 1 ? '' : 's'}
              </span>
            </>
          )}
          {inv.source_provider && (
            <>
              <span>·</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700 ring-1 ring-inset ring-indigo-200">
                {inv.source_provider}
              </span>
            </>
          )}
          <span>·</span>
          <span>sent {formatRelative(inv.created_at)}</span>
          {clerkMissing && (
            <>
              <span>·</span>
              <span
                title="The original Clerk invitation didn't register — the recipient probably didn't receive an email. Resend to retry."
                className="inline-flex items-center gap-1 rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700 ring-1 ring-inset ring-red-200"
              >
                <AlertTriangle className="h-2.5 w-2.5" />
                no email sent
              </span>
            </>
          )}
        </div>
      </div>
      <RoleGate allow={['admin']} fallback={null}>
        <button
          type="button"
          disabled={resend.isPending || revoke.isPending}
          onClick={() => {
            if (window.confirm(`Resend invitation to ${inv.email}?`)) {
              resend.mutate(
                { id: inv.id, redirect_url: clerkRedirectUrl() },
                {
                  onError: (err) => {
                    window.alert(`Resend failed: ${(err as Error).message}`);
                  },
                },
              );
            }
          }}
          className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs text-brand-purple hover:bg-purple-50 disabled:opacity-40"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', resend.isPending && 'animate-spin')} />
          {resend.isPending ? 'Resending…' : 'Resend'}
        </button>
        <button
          type="button"
          disabled={revoke.isPending || resend.isPending}
          onClick={() => {
            if (window.confirm(`Revoke invitation for ${inv.email}?`)) {
              revoke.mutate(inv.id);
            }
          }}
          className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs text-red-700 hover:bg-red-50 disabled:opacity-40"
        >
          <Undo2 className="h-3.5 w-3.5" />
          Revoke
        </button>
      </RoleGate>
    </li>
  );
}

const APP_ROLES: AppRole[] = ['worker', 'monitor', 'admin'];

interface DraftInvite {
  id: string;
  email: string;
  phone_e164: string;
  app_role: AppRole;
  team_ids: string[];
}

function newDraft(): DraftInvite {
  return {
    id: crypto.randomUUID(),
    email: '',
    phone_e164: '',
    app_role: 'worker',
    team_ids: [],
  };
}

function InviteModal({ onClose }: { onClose: () => void }) {
  const teams = useTeamsList();
  const create = useCreateInvitations();
  const [drafts, setDrafts] = useState<DraftInvite[]>([newDraft()]);
  const [sharedTeamIds, setSharedTeamIds] = useState<string[]>([]);
  const [sharedRole, setSharedRole] = useState<AppRole>('worker');
  const [outcomes, setOutcomes] = useState<CreateInvitationOutcome[] | null>(null);

  function update(i: number, patch: Partial<DraftInvite>) {
    setDrafts((prev) => prev.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  }

  function toggleSharedTeam(id: string) {
    setSharedTeamIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Apply shared role/teams unless the draft's row was individually customised.
    const items: InviteItem[] = drafts
      .filter((d) => d.email.trim().length > 0)
      .map((d) => ({
        email: d.email.trim(),
        phone_e164: d.phone_e164.trim() || undefined,
        app_role: d.app_role ?? sharedRole,
        team_ids: d.team_ids.length > 0 ? d.team_ids : sharedTeamIds,
      }));
    if (items.length === 0) return;
    // Always pass the dashboard's origin as the Clerk invitation redirect URL.
    // Without this, Clerk falls back to whatever's configured in its application
    // settings — historically that pointed at the marketing site, which has no
    // Clerk JS, so the __clerk_ticket in the email link never gets consumed and
    // invitees land on a dead page (issue observed 2026-05-11).
    const res = await create.mutateAsync({
      invitations: items,
      redirect_url: clerkRedirectUrl(),
    });
    setOutcomes(res.outcomes);
  }

  const canSubmit = drafts.some((d) => d.email.trim().length > 2);
  const allTeams = teams.data?.teams ?? [];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="inv-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        role="document"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-lg bg-white p-5 shadow-2xl"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 id="inv-title" className="text-base font-semibold text-ink">
              Invite workers
            </h2>
            <p className="mt-0.5 text-xs text-ink-faint">
              Clerk sends the invite emails. On accept, we auto-assign the app role, teams and phone
              number you set here.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-ink-mute hover:bg-surface-3 hover:text-ink-dim"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {outcomes ? (
          <OutcomesView outcomes={outcomes} onClose={onClose} />
        ) : (
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="mb-1 text-xs font-medium text-ink-dim">Default role</div>
                <div className="flex flex-wrap gap-1">
                  {APP_ROLES.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => {
                        setSharedRole(r);
                        setDrafts((prev) => prev.map((d) => ({ ...d, app_role: r })));
                      }}
                      className={cn(
                        'rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize transition',
                        sharedRole === r
                          ? 'border-brand-purple bg-brand-purple text-white'
                          : 'border-line bg-white text-ink-dim hover:border-line-strong',
                      )}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="mb-1 text-xs font-medium text-ink-dim">Teams</div>
                {allTeams.length === 0 ? (
                  <span className="text-xs text-ink-mute">No teams yet.</span>
                ) : (
                  <div className="flex max-h-20 flex-wrap gap-1 overflow-y-auto">
                    {allTeams.map((t) => {
                      const active = sharedTeamIds.includes(t.id);
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => toggleSharedTeam(t.id)}
                          className={cn(
                            'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]',
                            active
                              ? 'border-brand-purple bg-brand-purple text-white'
                              : 'border-line bg-white text-ink-dim hover:border-line-strong',
                          )}
                        >
                          <span
                            aria-hidden
                            className="h-1.5 w-1.5 rounded-full"
                            style={{
                              backgroundColor: active ? '#ffffff' : (t.colour ?? '#4C12A1'),
                            }}
                          />
                          {t.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div>
              <div className="mb-1 flex items-center justify-between">
                <div className="text-xs font-medium text-ink-dim">Emails + optional phone</div>
                <button
                  type="button"
                  onClick={() => setDrafts((prev) => [...prev, newDraft()])}
                  className="text-xs text-brand-purple hover:underline"
                >
                  + Add another
                </button>
              </div>
              <div className="flex flex-col gap-1.5">
                {drafts.map((d, i) => (
                  <div key={d.id} className="flex items-center gap-2">
                    <input
                      type="email"
                      value={d.email}
                      onChange={(e) => update(i, { email: e.target.value })}
                      placeholder="name@example.com"
                      className="flex-1 rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
                    />
                    <input
                      type="tel"
                      value={d.phone_e164}
                      onChange={(e) => update(i, { phone_e164: e.target.value })}
                      placeholder="+61 412 345 678"
                      className="w-44 rounded-md border border-line-strong px-3 py-1.5 font-mono text-xs focus:border-brand-purple focus:outline-none"
                    />
                    {drafts.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setDrafts((prev) => prev.filter((_, idx) => idx !== i))}
                        className="rounded p-1 text-ink-mute hover:bg-surface-3 hover:text-ink-dim"
                        aria-label="Remove"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {create.isError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                {(create.error as Error).message}
              </div>
            )}

            <div className="flex justify-end gap-2 border-t border-line pt-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-line bg-white px-3 py-1.5 text-sm font-medium text-ink-dim hover:bg-surface-3"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSubmit || create.isPending}
                className="inline-flex items-center gap-1.5 rounded-md bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-deep-purple disabled:opacity-50"
              >
                {create.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <MailPlus className="h-3.5 w-3.5" />
                )}
                Send {drafts.filter((d) => d.email.trim()).length > 1 ? 'invites' : 'invite'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function OutcomesView({
  outcomes,
  onClose,
}: {
  outcomes: CreateInvitationOutcome[];
  onClose: () => void;
}) {
  const created = outcomes.filter((o) => o.status === 'created');
  const existingMembers = outcomes.filter((o) => o.status === 'already_member');
  const existingPending = outcomes.filter((o) => o.status === 'already_pending');
  const failed = outcomes.filter((o) => o.status === 'failed');

  return (
    <div className="flex flex-col gap-3">
      {created.length > 0 && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          <div className="font-semibold">
            Sent {created.length} invitation{created.length === 1 ? '' : 's'}
          </div>
          <ul className="mt-1 list-disc pl-5 text-xs">
            {created.map((o) => (
              <li key={o.status === 'created' ? o.invitation.email : ''}>
                {o.status === 'created' && o.invitation.email}
              </li>
            ))}
          </ul>
        </div>
      )}
      {existingMembers.length > 0 && (
        <div className="rounded-md border border-line bg-surface-3 px-3 py-2 text-xs text-ink-dim">
          Already members:{' '}
          {existingMembers
            .map((o) => o.status === 'already_member' && o.email)
            .filter(Boolean)
            .join(', ')}
        </div>
      )}
      {existingPending.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Already have a pending invite:{' '}
          {existingPending
            .map((o) => o.status === 'already_pending' && o.invitation.email)
            .filter(Boolean)
            .join(', ')}
        </div>
      )}
      {failed.length > 0 && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          <div className="font-semibold">Failed:</div>
          <ul className="mt-1 list-disc pl-5">
            {failed.map(
              (o) =>
                o.status === 'failed' && (
                  <li key={o.email}>
                    {o.email} — {o.reason}
                  </li>
                ),
            )}
          </ul>
        </div>
      )}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-deep-purple"
        >
          Done
        </button>
      </div>
    </div>
  );
}

function Row({
  m,
  onRequestWelfareCheck,
  onEdit,
  onManageContacts,
  onLifecycle,
  isSelf,
}: {
  m: RosterMember;
  onRequestWelfareCheck: (member: RosterMember) => void;
  onEdit: (member: RosterMember) => void;
  onManageContacts: (member: RosterMember) => void;
  onLifecycle: (member: RosterMember, action: LifecycleAction) => void;
  isSelf: boolean;
}) {
  // Colour the active check-in by urgency so an imminent/overdue one stands out.
  const checkInDueMs = m.active_check_in_scheduled_for
    ? new Date(m.active_check_in_scheduled_for).getTime() - Date.now()
    : null;
  const checkInOverdue = checkInDueMs != null && checkInDueMs < 0;
  const checkInImminent = checkInDueMs != null && checkInDueMs >= 0 && checkInDueMs < 5 * 60_000;
  return (
    <tr
      className={cn(
        'hover:bg-surface-3',
        m.active_alarm_id && 'bg-red-50/40',
        !m.active && 'opacity-60',
      )}
    >
      <Td>
        <div className="flex items-center gap-3">
          <Avatar name={m.display_name} url={m.avatar_url} />
          <div>
            <div className="flex items-center gap-2 font-medium text-ink">
              {m.display_name}
              {!m.active && (
                <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 ring-1 ring-inset ring-gray-200">
                  Deactivated
                </span>
              )}
            </div>
            <div className="text-xs text-ink-faint">{m.email}</div>
            {m.phone_e164 && (
              <a
                href={`tel:${m.phone_e164}`}
                className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:underline"
                title={`Call ${m.display_name}`}
              >
                <Phone className="h-3 w-3" />
                {m.phone_e164}
              </a>
            )}
            {(m.state || m.job_title) && (
              <div className="text-[11px] text-ink-mute">
                {[m.state, m.job_title].filter(Boolean).join(' ')}
              </div>
            )}
          </div>
        </div>
      </Td>
      <Td>
        <span
          className={cn(
            'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
            m.app_role === 'admin'
              ? 'bg-purple-50 text-purple-700 ring-purple-200'
              : m.app_role === 'monitor'
                ? 'bg-blue-50 text-blue-700 ring-blue-200'
                : 'bg-surface-3 text-ink-dim ring-gray-200',
          )}
        >
          {m.app_role}
        </span>
      </Td>
      <Td>
        {m.teams.length === 0 ? (
          <span className="text-xs text-ink-mute">—</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {m.teams.map((t) => (
              <Link
                key={t.id}
                to={`/teams/${t.id}`}
                className="inline-flex items-center gap-1 rounded-full bg-surface-3 px-2 py-0.5 text-xs font-medium text-ink-dim ring-1 ring-inset ring-gray-200 hover:bg-gray-200"
              >
                <span
                  aria-hidden="true"
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: t.colour ?? '#4C12A1' }}
                />
                {t.name}
              </Link>
            ))}
          </div>
        )}
      </Td>
      <Td>
        <div className="flex flex-col gap-1">
          {m.active_alarm_id ? (
            <Link
              to={`/alarms/${m.active_alarm_id}`}
              className="inline-flex items-center gap-1 text-sm text-red-700 hover:underline"
            >
              <ShieldAlert className="h-4 w-4" />
              {m.active_alarm_type ? formatAlarmType(m.active_alarm_type) : 'Active alarm'}
            </Link>
          ) : m.active_check_in_id ? (
            <span
              className={cn(
                'inline-flex items-center gap-1 text-sm',
                checkInOverdue
                  ? 'text-red-700'
                  : checkInImminent
                    ? 'text-amber-700'
                    : 'text-blue-700',
              )}
            >
              <CheckCircle2 className="h-4 w-4" />
              Active check-in
              {m.active_check_in_scheduled_for && (
                <span
                  className={cn(
                    'text-xs',
                    checkInOverdue ? 'font-semibold text-red-700' : 'text-ink-faint',
                  )}
                >
                  · {checkInOverdue ? 'overdue' : 'due'}{' '}
                  {formatRelative(m.active_check_in_scheduled_for)}
                </span>
              )}
            </span>
          ) : (
            <span className="text-sm text-ink-mute">—</span>
          )}
        </div>
      </Td>
      <Td className="text-sm text-ink-faint">
        {m.last_seen_at ? (
          formatRelative(m.last_seen_at)
        ) : (
          <span className="text-ink-mute">Never</span>
        )}
      </Td>
      <Td className="text-sm text-ink-faint">{formatRelative(m.joined_at)}</Td>
      <Td className="text-right">
        {/* Action icons — labels hidden, surfaced via native title tooltip on hover.
            Rationale: with 5 inline labelled buttons the row overflowed on
            standard 1440px screens, hiding the destructive "Delete" off-frame. */}
        <div className="inline-flex items-center gap-1">
          <RoleGate allow={['admin']} fallback={null}>
            <IconAction
              icon={<Pencil className="h-3.5 w-3.5" />}
              label="Edit profile"
              onClick={() => onEdit(m)}
            />
            <IconAction
              icon={<Phone className="h-3.5 w-3.5" />}
              label="Manage emergency contacts"
              onClick={() => onManageContacts(m)}
            />
          </RoleGate>
          {m.app_role === 'worker' && (
            <IconAction
              icon={<HeartPulse className="h-3.5 w-3.5" />}
              label="Request welfare check"
              onClick={() => onRequestWelfareCheck(m)}
            />
          )}
          <RoleGate allow={['admin']} fallback={null}>
            {!isSelf &&
              (m.active ? (
                <IconAction
                  icon={<PowerOff className="h-3.5 w-3.5" />}
                  label="Deactivate user (revokes Clerk sessions; reversible)"
                  onClick={() => onLifecycle(m, 'deactivate')}
                  hoverClass="hover:border-amber-500 hover:text-amber-700"
                />
              ) : (
                <IconAction
                  icon={<Power className="h-3.5 w-3.5" />}
                  label="Reactivate user (restores Clerk access)"
                  onClick={() => onLifecycle(m, 'reactivate')}
                  hoverClass="hover:border-emerald-500 hover:text-emerald-700"
                />
              ))}
            {!isSelf && (
              <IconAction
                icon={<Trash2 className="h-3.5 w-3.5" />}
                label="Hard-delete user (irreversible; removes Clerk account + local data)"
                onClick={() => onLifecycle(m, 'delete')}
                tone="danger"
              />
            )}
          </RoleGate>
        </div>
      </Td>
    </tr>
  );
}

// Compact icon-only action button. Uses the native `title` attribute for the
// hover tooltip — keeps the row dense enough to fit 5 actions on a 1440px
// screen without overflowing the table.
function IconAction({
  icon,
  label,
  onClick,
  hoverClass = 'hover:border-brand-purple hover:text-brand-purple',
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  hoverClass?: string;
  tone?: 'danger';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cn(
        'inline-flex h-7 w-7 items-center justify-center rounded-md border border-line bg-white text-ink-dim transition',
        tone === 'danger' ? 'text-red-700 hover:border-red-500 hover:bg-red-50' : hoverClass,
      )}
    >
      {icon}
    </button>
  );
}

// Combined modal for the three lifecycle actions. Deactivate / reactivate are
// simple confirms; delete additionally requires the admin to type the user's
// email exactly as a deliberate-action gate (matches GitHub / Stripe pattern).
function LifecycleModal({
  target,
  action,
  onClose,
}: {
  target: RosterMember;
  action: LifecycleAction;
  onClose: () => void;
}) {
  const deactivate = useDeactivateUser();
  const reactivate = useReactivateUser();
  const del = useDeleteUser();
  // Each hook has a different response type, so we can't widen them into one
  // `mutation` reference. Pick the relevant pending/error flags per-action.
  const isPending = deactivate.isPending || reactivate.isPending || del.isPending;
  const error =
    action === 'delete' ? del.error : action === 'reactivate' ? reactivate.error : deactivate.error;
  const isError =
    action === 'delete'
      ? del.isError
      : action === 'reactivate'
        ? reactivate.isError
        : deactivate.isError;

  const [confirmInput, setConfirmInput] = useState('');
  const confirmMatches = action !== 'delete' || confirmInput.trim() === target.email;

  const title =
    action === 'delete'
      ? `Delete ${target.display_name}?`
      : action === 'reactivate'
        ? `Reactivate ${target.display_name}?`
        : `Deactivate ${target.display_name}?`;

  function submit() {
    if (!confirmMatches) return;
    if (action === 'delete') del.mutate(target.user_id, { onSuccess: onClose });
    else if (action === 'reactivate') reactivate.mutate(target.user_id, { onSuccess: onClose });
    else deactivate.mutate(target.user_id, { onSuccess: onClose });
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="lifecycle-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        role="document"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg border border-line bg-surface-1 p-5 shadow-panel"
      >
        <h2
          id="lifecycle-title"
          className="font-heading text-base font-bold tracking-tight text-ink"
        >
          {title}
        </h2>
        <div className="mt-3 space-y-2 text-sm text-ink-faint">
          {action === 'deactivate' && (
            <>
              <p>
                This will revoke <span className="font-medium text-ink">{target.display_name}</span>
                's Clerk sessions and block them from signing in. Their data (alarms, check-ins,
                audit history) is preserved and you can reactivate them at any time.
              </p>
              <p className="text-xs">
                Email <span className="font-mono text-ink-dim">{target.email}</span> · role{' '}
                <span className="font-mono text-ink-dim">{target.app_role}</span>
              </p>
            </>
          )}
          {action === 'reactivate' && (
            <p>
              This un-bans <span className="font-medium text-ink">{target.display_name}</span> in
              Clerk and restores their access. They can sign in immediately after.
            </p>
          )}
          {action === 'delete' && (
            <>
              <p className="text-red-700">
                <Trash2 className="-mt-0.5 mr-1 inline h-3.5 w-3.5" />
                <span className="font-semibold">This is irreversible.</span> The Clerk account is
                purged, local profile and team memberships are deleted, and any future visits /
                check-ins / alarms with this user_id are dropped. Audit log entries are kept with
                actor_id = NULL for compliance.
              </p>
              <p>
                Use <span className="font-medium">Deactivate</span> instead if you only need to
                block sign-in.
              </p>
              <label
                htmlFor="lifecycle-confirm-email"
                className="mt-2 block text-xs font-medium text-ink-faint"
              >
                Type <span className="font-mono text-ink-dim">{target.email}</span> to confirm
              </label>
              <input
                id="lifecycle-confirm-email"
                type="email"
                autoComplete="off"
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                placeholder={target.email}
                className="mt-1 w-full rounded-md border border-line-strong px-3 py-2 font-mono text-sm placeholder-gray-300 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
              />
            </>
          )}
        </div>
        {isError && (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
            {(error as Error).message}
          </div>
        )}
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="rounded-md border border-line bg-surface-1 px-3 py-1.5 text-sm font-medium text-ink-dim transition hover:border-line-strong hover:bg-surface-3 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={isPending || !confirmMatches}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-white shadow-sm transition disabled:opacity-50',
              action === 'delete'
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-brand-purple hover:bg-brand-deep-purple',
            )}
          >
            {action === 'delete' && <Trash2 className="h-3.5 w-3.5" />}
            {action === 'reactivate' && <Power className="h-3.5 w-3.5" />}
            {action === 'deactivate' && <PowerOff className="h-3.5 w-3.5" />}
            {isPending
              ? `${action === 'delete' ? 'Deleting' : action === 'reactivate' ? 'Reactivating' : 'Deactivating'}…`
              : action === 'delete'
                ? 'Delete forever'
                : action === 'reactivate'
                  ? 'Reactivate'
                  : 'Deactivate'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditUserModal({
  target,
  onClose,
}: {
  target: RosterMember;
  onClose: () => void;
}) {
  const teams = useTeamsList();
  const jobTitles = useJobTitles();
  const update = useUpdateUser();
  const [displayName, setDisplayName] = useState(target.display_name);
  const [phone, setPhone] = useState(target.phone_e164 ?? '');
  const [state, setStateValue] = useState<AustralianState | ''>(target.state ?? '');
  // Free string — the per-org list lives in organisation_job_titles, fetched
  // via useJobTitles. The worker's currently-stored value may be one that's
  // since been archived; we still pre-fill it so the form reflects reality,
  // but the user can only re-select from the active list.
  const [jobTitle, setJobTitleValue] = useState<string>(target.job_title ?? '');
  const [isWorker, setIsWorker] = useState<boolean>(target.is_worker);
  const [isMonitor, setIsMonitor] = useState<boolean>(target.is_monitor);
  const [teamIds, setTeamIds] = useState<string[]>(target.teams.map((t) => t.id));
  const [error, setError] = useState<{ message: string; conflicting_user_id?: string } | null>(
    null,
  );

  const allTeams = teams.data?.teams ?? [];

  const capsChanged =
    !target.is_admin && (isWorker !== target.is_worker || isMonitor !== target.is_monitor);
  // Submit must never end up with both capabilities false on a non-admin
  // (the server rejects with no_capability; we mirror that in the form).
  const capsValid = target.is_admin || isWorker || isMonitor;
  const dirty =
    displayName.trim() !== target.display_name ||
    (phone.trim() || null) !== (target.phone_e164 ?? null) ||
    (state || null) !== (target.state ?? null) ||
    (jobTitle || null) !== (target.job_title ?? null) ||
    capsChanged ||
    !arrayEqual(
      teamIds,
      target.teams.map((t) => t.id),
    );

  function toggleTeam(id: string) {
    setTeamIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await update.mutateAsync({
        user_id: target.user_id,
        display_name: displayName.trim() || undefined,
        // Send null explicitly when the field was cleared, undefined when unchanged.
        phone_e164:
          phone.trim() === (target.phone_e164 ?? '')
            ? undefined
            : phone.trim() === ''
              ? null
              : phone.trim(),
        state:
          (state || null) === (target.state ?? null)
            ? undefined
            : state === ''
              ? null
              : (state as AustralianState),
        job_title:
          (jobTitle || null) === (target.job_title ?? null)
            ? undefined
            : jobTitle === ''
              ? null
              : jobTitle,
        is_worker: !target.is_admin && isWorker !== target.is_worker ? isWorker : undefined,
        is_monitor: !target.is_admin && isMonitor !== target.is_monitor ? isMonitor : undefined,
        team_ids: arrayEqual(
          teamIds,
          target.teams.map((t) => t.id),
        )
          ? undefined
          : teamIds,
      });
      onClose();
    } catch (err) {
      const e2 = err as {
        message?: string;
        code?: string;
        details?: { conflicting_user_id?: string };
      };
      setError({
        message: e2.message ?? 'Failed to save changes.',
        conflicting_user_id: e2.details?.conflicting_user_id,
      });
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-user-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        role="document"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-lg bg-white p-5 shadow-2xl"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 id="edit-user-title" className="text-base font-semibold text-ink">
              Edit {target.display_name}
            </h2>
            <p className="mt-0.5 text-xs text-ink-faint">{target.email}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-ink-mute hover:bg-surface-3 hover:text-ink-dim"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-dim">Display name</span>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={120}
              className="rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-dim">Mobile number</span>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+61 412 345 678"
              maxLength={24}
              className="rounded-md border border-line-strong px-3 py-1.5 font-mono text-sm focus:border-brand-purple focus:outline-none"
            />
            <span className="text-[11px] text-ink-faint">
              Use full international format (e.g. +61412345678). Numbers must be unique.
            </span>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-ink-dim">State</span>
              <select
                value={state}
                onChange={(e) => setStateValue(e.target.value as AustralianState | '')}
                className="rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
              >
                <option value="">—</option>
                {AUSTRALIAN_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-ink-dim">Job title</span>
              <select
                value={jobTitle}
                onChange={(e) => setJobTitleValue(e.target.value)}
                className="rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
              >
                <option value="">—</option>
                {/* When the worker already holds an archived value, render it
                    here so the dropdown reflects the current state. */}
                {jobTitle && !jobTitles.data?.job_titles.some((t) => t.name === jobTitle) && (
                  <option value={jobTitle}>{jobTitle} (archived)</option>
                )}
                {jobTitles.data?.job_titles.map((t) => (
                  <option key={t.id} value={t.name}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div>
            <div className="mb-1 text-xs font-medium text-ink-dim">Permissions</div>
            {target.is_admin ? (
              // Admin status is sourced from Clerk org_role; surfaced here as a
              // read-only badge so an admin can't accidentally demote themselves
              // via this form. Promotion / demotion to admin happens via Clerk's
              // organisation membership UI.
              <div className="inline-flex items-center gap-2 rounded-md border border-brand-purple/30 bg-brand-purple/10 px-3 py-1.5 text-xs text-brand-purple">
                <ShieldAlert className="h-3.5 w-3.5" />
                <span className="font-medium">Admin</span>
                <span className="text-ink-faint">
                  · managed via Clerk; implies Worker + Monitor
                </span>
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                <label className="inline-flex items-center gap-2 text-xs text-ink-dim">
                  <input
                    type="checkbox"
                    checked={isWorker}
                    onChange={(e) => setIsWorker(e.target.checked)}
                    className="h-3.5 w-3.5"
                  />
                  <span>
                    <span className="font-medium text-ink">Worker</span>
                    <span className="text-ink-faint"> · uses the mobile app</span>
                  </span>
                </label>
                <label className="inline-flex items-center gap-2 text-xs text-ink-dim">
                  <input
                    type="checkbox"
                    checked={isMonitor}
                    onChange={(e) => setIsMonitor(e.target.checked)}
                    className="h-3.5 w-3.5"
                  />
                  <span>
                    <span className="font-medium text-ink">Monitor</span>
                    <span className="text-ink-faint"> · sees alarms + the dashboard</span>
                  </span>
                </label>
                {!isWorker && !isMonitor && (
                  <p className="text-[11px] text-red-600">
                    Pick at least one — a user must be a Worker, Monitor, or both.
                  </p>
                )}
              </div>
            )}
          </div>

          <div>
            <div className="mb-1 text-xs font-medium text-ink-dim">Teams</div>
            {allTeams.length === 0 ? (
              <span className="text-xs text-ink-mute">No teams yet.</span>
            ) : (
              <div className="flex flex-wrap gap-1">
                {allTeams.map((t) => {
                  const active = teamIds.includes(t.id);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => toggleTeam(t.id)}
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]',
                        active
                          ? 'border-brand-purple bg-brand-purple text-white'
                          : 'border-line bg-white text-ink-dim hover:border-line-strong',
                      )}
                    >
                      <span
                        aria-hidden
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ backgroundColor: active ? '#ffffff' : (t.colour ?? '#4C12A1') }}
                      />
                      {t.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="border-t border-line pt-3">
            <div className="mb-2 text-xs font-medium text-ink-dim">Roster system IDs</div>
            <ExternalIdentitiesSection userId={target.user_id} />
            <p className="mt-1 text-[10px] text-ink-faint">
              Link this worker to their AlayaCare / ShiftCare employee id so webhook visit events
              can route to them.
            </p>
          </div>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
              {error.message}
            </div>
          )}

          <div className="flex justify-end gap-2 border-t border-line pt-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-line bg-white px-3 py-1.5 text-sm font-medium text-ink-dim hover:bg-surface-3"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!dirty || !capsValid || update.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-deep-purple disabled:opacity-50"
            >
              {update.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Pencil className="h-3.5 w-3.5" />
              )}
              Save changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function arrayEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortA = [...a].sort();
  const sortB = [...b].sort();
  return sortA.every((v, i) => v === sortB[i]);
}

const WELFARE_DURATION_OPTIONS = [10, 15, 30, 60] as const;

function WelfareCheckModal({
  target,
  onClose,
}: {
  target: RosterMember;
  onClose: () => void;
}) {
  const [duration, setDuration] = useState<number>(15);
  const [message, setMessage] = useState<string>('');
  const request = useRequestWelfareCheck();
  const [result, setResult] = useState<{
    pushSent: number;
    pushFailed: number;
    durationMinutes: number;
  } | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const res = await request.mutateAsync({
      userId: target.user_id,
      durationMinutes: duration,
      message: message.trim() || undefined,
    });
    setResult({
      pushSent: res.push_sent,
      pushFailed: res.push_failed,
      durationMinutes: res.check_in.duration_minutes,
    });
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="welfare-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        role="document"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg bg-white p-5 shadow-2xl"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 id="welfare-title" className="text-base font-semibold text-ink">
              Request welfare check
            </h2>
            <p className="mt-0.5 text-xs text-ink-faint">
              {target.display_name} will get a push notification to confirm they're OK. If they
              don't respond within the window, the usual overdue alarm fires.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-ink-mute hover:bg-surface-3 hover:text-ink-dim"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {result ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
              <BellRing className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <div className="font-semibold">Welfare check sent</div>
                <div className="mt-0.5 text-xs">
                  {result.pushSent} push{result.pushSent === 1 ? '' : 'es'} delivered
                  {result.pushFailed > 0 ? `, ${result.pushFailed} failed` : ''} ·{' '}
                  {result.durationMinutes}-min window.
                </div>
              </div>
            </div>
            {result.pushSent === 0 && target.phone_e164 && (
              <div className="text-xs text-ink-faint">
                No push went through (likely no registered device). Consider SMS to{' '}
                <span className="font-mono">{target.phone_e164}</span> as a fallback.
              </div>
            )}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-deep-purple"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <div>
              <div className="mb-1 text-xs font-medium text-ink-dim">Window</div>
              <div className="flex flex-wrap gap-2">
                {WELFARE_DURATION_OPTIONS.map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setDuration(m)}
                    className={cn(
                      'rounded-full border px-3 py-1 text-xs font-medium transition',
                      duration === m
                        ? 'border-brand-purple bg-brand-purple text-white'
                        : 'border-line bg-white text-ink-dim hover:border-line-strong',
                    )}
                  >
                    {m < 60 ? `${m} min` : `${m / 60}h`}
                  </button>
                ))}
              </div>
            </div>

            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-ink-dim">Message (optional)</span>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={300}
                rows={3}
                placeholder="Please confirm you are OK."
                className="rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
              />
            </label>

            {request.isError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                {(request.error as Error).message}
              </div>
            )}

            <div className="flex justify-end gap-2 border-t border-line pt-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-line bg-white px-3 py-1.5 text-sm font-medium text-ink-dim hover:bg-surface-3"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={request.isPending}
                className="inline-flex items-center gap-1.5 rounded-md bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-deep-purple disabled:opacity-50"
              >
                {request.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <HeartPulse className="h-3.5 w-3.5" />
                )}
                Send welfare check
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Avatar({ name, url }: { name: string; url: string | null }) {
  const initials = name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  if (url) {
    return (
      <img
        src={url}
        alt={name}
        className="h-9 w-9 rounded-full object-cover ring-1 ring-gray-200"
      />
    );
  }
  return (
    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-purple/10 font-medium text-brand-purple ring-1 ring-brand-purple/20">
      {initials || '?'}
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cn(
        'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-ink-faint',
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-4 py-3 text-sm', className)}>{children}</td>;
}
